import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUp, ArrowRight, Sparkles, Video, GraduationCap, Radio } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtCompact } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { CompanyLogo } from "@/components/common";
import { STEPS } from "@/components/workspace/StepRail";

const stepLabel = (k) => STEPS.find((s) => s.key === k)?.label || k;

const EXAMPLES = [
  { icon: Video, label: "Corporate studio", hint: "Hybrid town halls, NDI, Teams", text: "Use example inquiry" },
  {
    icon: GraduationCap,
    label: "Lecture capture",
    hint: "6 auditoriums, no operator",
    text: "Hei, Universitetet i Bergen (Faculty of Social Sciences) is evaluating lecture capture for 6 auditoriums. We need automatic recording of every lecture with the lecturer tracked by camera, slides captured, and the recordings published to our LMS (Canvas) within an hour. No operator. Budget frame about 2.5 MNOK over two years. Pilot in two rooms this autumn. Regards, Kari Nordmann, IT Director",
  },
  {
    icon: Radio,
    label: "Town hall streaming",
    hint: "Two cameras, Teams, 8 weeks",
    text: "Hi David, Storebrand wants to stream monthly town halls from the Lysaker auditorium to 2 000 employees on Teams and record them. Two cameras, wireless mics for the CEO and Q&A, simple operation by the comms team, must be ready in 8 weeks. Budget around 900 kNOK. Best, Ola Hansen",
  },
];

function Chip({ onClick, disabled, logoName, logoDomain, title, meta, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="group flex h-11 shrink-0 items-center gap-2.5 rounded-full border border-black/[0.07] bg-white pl-1.5 pr-4 text-left transition-colors duration-150 hover:border-black/20 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-ring/40"
    >
      <CompanyLogo name={logoName} domain={logoDomain} size="sm" className="h-8 w-8 rounded-full" />
      <span className="min-w-0 leading-tight">
        <span className="block max-w-[220px] truncate text-[13px] font-medium">{title}</span>
        <span className="block text-[11px] text-muted-foreground">{meta}</span>
      </span>
      <ArrowRight className="ml-1 h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
    </button>
  );
}

/**
 * The empty workspace: one question, one big composer, three examples. Purely presentational -
 * the page below wires it to the API; the proposal's preview drives it with a script.
 * `active` draws the composer as focused, `pressing` presses the start button (used by the preview).
 */
export function WorkspaceStart({ first, text, onChange, onStart, onExample, busy, examples = EXAMPLES, active = false, pressing = false, testIdPrefix = "workspace-home" }) {
  const taRef = useRef(null);
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "0px";
    /* two lines of placeholder on a narrow screen – one on a wide one */
    const min = ta.clientWidth < 440 ? 72 : 56;
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, min), 200)}px`;
  }, [text]);
  const go = () => text.trim() && !busy && onStart(text.trim());
  return (
    <div className="w-full max-w-[720px]" data-testid={`${testIdPrefix}-start`}>
      <div className="text-center">
        <h1 className="font-display text-[30px] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-[40px]" data-testid={`${testIdPrefix}-title`}>
          What are we selling today{first ? `, ${first}` : ""}?
        </h1>
        <p className="mt-3 text-[14.5px] text-muted-foreground">Paste an inquiry - I'll design, price and write the proposal with you.</p>
      </div>

      <div
        className={cn(
          "mt-8 rounded-[26px] border border-black/[0.09] bg-white shadow-[0_1px_2px_hsl(30_12%_18%/0.04),0_20px_50px_-30px_hsl(30_12%_18%/0.45)] transition-[border-color,box-shadow] duration-200 focus-within:border-black/[0.22] focus-within:shadow-[0_1px_2px_hsl(30_12%_18%/0.05),0_24px_60px_-30px_hsl(30_12%_18%/0.5)]",
          active && "border-black/[0.22] shadow-[0_1px_2px_hsl(30_12%_18%/0.05),0_24px_60px_-30px_hsl(30_12%_18%/0.5)]"
        )}
        data-testid={`${testIdPrefix}-composer`}
      >
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              go();
            }
          }}
          placeholder="Paste the customer's e-mail, tender excerpt or your meeting notes…"
          className="block w-full resize-none bg-transparent px-5 pt-4 text-[15px] leading-7 outline-none placeholder:text-muted-foreground/55"
          data-testid={`${testIdPrefix}-input`}
          aria-label="Inquiry text"
        />
        <div className="flex items-center justify-between gap-3 px-3 pb-3 pt-1">
          <span className="pl-2 text-[11.5px] text-muted-foreground/60">Enter to start · Shift+Enter for a new line</span>
          <button
            type="button"
            onClick={go}
            disabled={!text.trim() || busy}
            className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--ink))] text-white transition-[transform,opacity] duration-150 hover:scale-[1.05] disabled:opacity-25 disabled:hover:scale-100 focus-visible:ring-2 focus-visible:ring-ring/50",
              pressing && "scale-[0.9]"
            )}
            aria-label="Start"
            data-testid={`${testIdPrefix}-start-button`}
          >
            <ArrowUp className="h-4 w-4" strokeWidth={2.4} />
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid={`${testIdPrefix}-examples`}>
        {examples.map((ex) => (
          <button
            key={ex.label}
            type="button"
            onClick={() => onExample(ex)}
            disabled={busy}
            className="group flex items-center gap-3 rounded-[16px] border border-black/[0.07] bg-white/70 px-3.5 py-3 text-left transition-colors duration-150 hover:border-black/20 hover:bg-white disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-ring/40"
            data-testid={`${testIdPrefix}-example`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(40_12%_95%)] text-muted-foreground transition-colors group-hover:text-primary">
              <ex.icon className="h-4 w-4" strokeWidth={1.9} />
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-[13px] font-medium">{ex.label}</span>
              <span className="block truncate text-[11.5px] text-muted-foreground">{ex.hint}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function WorkspaceHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [opps, setOpps] = useState([]);

  useEffect(() => {
    api.get("/workspace").then((r) => setSessions((r.data || []).filter((s) => s.opportunity_id).slice(0, 4))).catch(() => setSessions([]));
    api.get("/opportunities").then((r) => setOpps((r.data || []).filter((o) => o.status === "open").slice(0, 5))).catch(() => setOpps([]));
  }, []);

  const start = async (initial, opportunityId) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/workspace", { opportunity_id: opportunityId || null });
      navigate(`/workspace/${r.data.session.id}`, { state: initial ? { initial } : null });
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const first = (user?.name || "").split(" ")[0];
  const recentOppIds = new Set(sessions.map((s) => s.opportunity_id));
  const continueOpps = opps.filter((o) => !recentOppIds.has(o.id)).slice(0, 4);

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="workspace-home">
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-5 sm:px-8">
        <WorkspaceStart first={first} text={text} onChange={setText} onStart={(t) => start(t)} onExample={(ex) => start(ex.text)} busy={busy} />
      </div>

      {(sessions.length > 0 || continueOpps.length > 0) && (
        <div className="shrink-0 px-5 pb-6 sm:px-8" data-testid="workspace-home-continue">
          <div className="mx-auto max-w-[1080px]">
            <div className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              <Sparkles className="h-3 w-3 text-primary" /> Pick up where you left off
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {sessions.map((s) => (
                <Chip key={s.id} onClick={() => navigate(`/workspace/${s.id}`)} logoName={s.account_name} logoDomain={s.account_website} title={s.title || "Workspace"} meta={`Workspace · ${stepLabel(s.step)}`} testId="workspace-home-session" />
              ))}
              {continueOpps.map((o) => (
                <Chip key={o.id} onClick={() => start(null, o.id)} disabled={busy} logoName={o.account_name} logoDomain={o.account_website} title={`${o.account_name} / ${o.name}`} meta={`${(o.stage || "").replace("_", " ")} · ${fmtCompact(o.value)}`} testId="workspace-home-opportunity" />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
